import {
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  SlideFade,
  VStack,
  Image,
  useColorModeValue,
  FormHelperText,
  useToast,
} from '@chakra-ui/react';
import type { FirebaseError } from 'firebase/app';
import { type ChangeEvent, type KeyboardEvent, useContext, useEffect, useState } from 'react';
import { errorToastOptions, getAuthErrorMessage } from '@/helpers';
import { isDefinitelyOffensiveName } from '@/helpers/moderation/nameModeration';
import { callUpdateUserName } from '@/helpers/moderation/updateUserName';
import bannerLight from '../assets/logos/bannerLight.svg';
import bannerDark from '../assets/logos/bannerDark.svg';
import { VIEWPORT_MIN_H } from '@/constants/mobileLayout';
import { FirebaseContext } from '@/session/FirebaseContext';
import { useUserProfile } from '@/session/useUserProfile';
import { isProfileReady } from '@/session/types';

function callableErrorMessage(error: FirebaseError): string {
  if (error.code === 'functions/internal' || error.code === 'functions/unavailable') {
    return 'The server could not save your name. Please try again in a moment.';
  }
  if (error.code === 'functions/failed-precondition') {
    return error.message || 'This action is not available right now.';
  }
  return getAuthErrorMessage(error);
}

export function EnterNamePage() {
  const ctx = useContext(FirebaseContext);
  const toast = useToast();
  const { userData, profilePhase } = useUserProfile();
  const [name, setName] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [nameError, setNameError] = useState('');
  const heroSrc = useColorModeValue(bannerLight, bannerDark);

  useEffect(() => {
    if (isBusy && isProfileReady(profilePhase) && (userData?.name ?? '').trim().length > 0) {
      setIsBusy(false);
    }
  }, [isBusy, profilePhase, userData]);

  async function onSubmit() {
    const trimmed = name.trim();
    if (trimmed.length === 0) return;

    if (isDefinitelyOffensiveName(trimmed)) {
      setNameError('That name is not allowed. Please choose another.');
      return;
    }

    setNameError('');
    setIsBusy(true);
    try {
      const result = await callUpdateUserName(ctx.funcs, trimmed);
      if (!result.accepted) {
        setNameError('That name is not allowed. Please choose another.');
        setIsBusy(false);
      }
    } catch (error) {
      console.error('[EnterNamePage]', error);
      const message = callableErrorMessage(error as FirebaseError);
      setNameError('Could not save your name. Please try again.');
      toast(errorToastOptions('Could not save name', message));
      setIsBusy(false);
    }
  }

  return (
    <Center w='full' minH={VIEWPORT_MIN_H} px={4}>
      <VStack spacing={4}>
        <Heading>Welcome to</Heading>
        <Image src={heroSrc} />
        <SlideFade in>
          <VStack spacing={4} mt={8}>
            <FormControl isInvalid={nameError.length > 0}>
              <FormLabel fontSize={18} textAlign='center'>
                What name would you like to use?
              </FormLabel>
              <Input
                textAlign={'center'}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === 'Enter') {
                    void onSubmit();
                  }
                }}
                value={name}
                isDisabled={isBusy}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setName(e.target.value);
                  if (nameError) setNameError('');
                }}
              />
              <FormHelperText>This will appear in all your leagues.</FormHelperText>
              {nameError ? <FormErrorMessage justifyContent='center'>{nameError}</FormErrorMessage> : null}
            </FormControl>
            <Button
              colorScheme={'green'}
              isDisabled={name.trim().length <= 0 || isBusy}
              isLoading={isBusy}
              onClick={() => void onSubmit()}
            >
              Submit
            </Button>
          </VStack>
        </SlideFade>
      </VStack>
    </Center>
  );
}
